import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@workspace/web-ui/components/table";
import { useState } from "react";
import { RefillPrescriptionHistory } from "~/features/dashboard/pages/medicals/types";
import { formatMedicationDate } from "@workspace/lib/date";
import { Button } from "@workspace/web-ui/components/button";
import { MedicationHistoryModal } from "./medication-history-modal";
import { Badge } from "@workspace/web-ui/components/badge";
import { Separator } from "@workspace/web-ui/components/separator";
import { Paginator } from "./pagination";

interface MedicalHistoryData {
  medicationHistory: RefillPrescriptionHistory[];
  totalCount?: number;
}

const ITEMS_PER_PAGE = 5; // Display 5 items per page

export const getRefillMedicationStatusConfig = (item: RefillPrescriptionHistory) => {
  const status = item.status

  const statusMap = {
    "completed": { color: "bg-[#EBFBF1] text-[#14AD4D] hover:bg-[#EBFBF1]", text: "Completed", status: "completed" },
    "rejected": { color: "bg-[#F1F1F1] text-[#7F8493] hover:bg-[#F1F1F1]", text: "Rejected", status: "rejected" },
  };

  return statusMap[status as keyof typeof statusMap];
};

export function MedicationHistory({ medicationHistory, totalCount }: MedicalHistoryData) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRefill, setSelectedRefill] = useState<RefillPrescriptionHistory | null>(null);
  const [showMedicationHistory, setShowMedicationHistory] = useState(false);

  // Calculate total pages
  const totalPages = Math.ceil(medicationHistory.length / ITEMS_PER_PAGE);

  // Get data for the current page
  const paginatedData = medicationHistory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="border rounded-xl shadow-sm bg-white w-full mb-4">
      <h3 className="flex items-center text-lg font-medium text-left p-5 text-[#1A202C]">Medication History <Badge className="ml-2 size-4 rounded-full bg-[#4272DD] border border-[#0404041A] p-2 justify-center items-center text-center text-white text-xs">
        {totalCount || 0}
      </Badge></h3>
      <Separator />

      {/* Table */}
      <Table className="w-full rounded-lg">
        <TableHeader className="">
          <TableRow className="border-y text-xs min-w-0">
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Medication</TableHead>
            <TableHead className="text-left text-[#202020CC] px-5 whitespace-nowrap">Ordered</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Delivered</TableHead>
            <TableHead className="text-left text-[#202020CC] whitespace-nowrap">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedData.map((item, index) => {
            const { color, text } = getRefillMedicationStatusConfig(item)
            return (
              <TableRow key={index} className={`border-0 p-7 text-[#2D2F35] text-sm`}>
                <TableCell>{item.medication}</TableCell>
                <TableCell>{formatMedicationDate(item.ordered_date)}</TableCell>
                <TableCell>{formatMedicationDate(item.delivery_date)}</TableCell>
                <TableCell>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${color}`}
                  >
                    {text}
                  </span>
                </TableCell>
                <TableCell>
                  <Button onClick={() => {
                    setSelectedRefill(item)
                    setShowMedicationHistory(true)
                  }}
                    variant={"outline"}>View</Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>


      <Paginator items={medicationHistory} totalPages={totalPages} currentPage={currentPage} onPageChange={setCurrentPage} itemsPerPage={ITEMS_PER_PAGE} className="border-t" />

      {selectedRefill && <MedicationHistoryModal refillMedication={selectedRefill} onClose={() => setShowMedicationHistory(false)} open={showMedicationHistory} />}
    </div>
  );
}
